import type { Achievement } from "@/common/types/vocab";
import { i18n } from "@/i18n";

import { type Question, QuestionTypeEnum } from "./composables/use-vocab-quiz";

export enum ModalType {
  Create = "create",
  AddParticipant = "add_participant"
}

export const createQuestionText = (
  type: QuestionTypeEnum,
  question: Question,
  language: "english" | "german"
): string => {
  const { t } = i18n.global;

  const questionTypePromptMap = {
    english: {
      [QuestionTypeEnum.MultipleChoice]: t("vocab.games.english.multipleChoicePrompt", {
        lemma: question.lemma,
        lemmaWordclass: question.lemmaWordclass ? `(${question.lemmaWordclass})` : ""
      }),
      [QuestionTypeEnum.TrueFalse]: t("vocab.games.english.trueFalsePrompt", {
        lemma: question.lemma,
        translation: question.option,
        lemmaWordclass: question.lemmaWordclass ? `(${question.lemmaWordclass})` : ""
      }),
      [QuestionTypeEnum.Writing]: t("vocab.games.english.writingPrompt", {
        translation: question.translation,
        translationWordclass: question.translationWordclass
          ? `(${question.translationWordclass})`
          : ""
      }),
      [QuestionTypeEnum.MatchingPairs]: t("vocab.games.english.matchingPairsPrompt"),
      [QuestionTypeEnum.DragDrop]: t("vocab.games.english.dragDropPrompt", {
        translation: question.translation,
        translationWordclass: question.translationWordclass
          ? `(${question.translationWordclass})`
          : ""
      })
    },
    german: {
      [QuestionTypeEnum.MultipleChoice]: t("vocab.games.german.multipleChoicePrompt", {
        lemma: question.lemma,
        lemmaWordclass: question.lemmaWordclass ? `(${question.lemmaWordclass})` : ""
      }),
      [QuestionTypeEnum.TrueFalse]: t("vocab.games.german.trueFalsePrompt", {
        lemma: question.lemma,
        translation: question.option,
        lemmaWordclass: question.lemmaWordclass ? `(${question.lemmaWordclass})` : ""
      }),
      [QuestionTypeEnum.Writing]: t("vocab.games.german.writingPrompt", {
        translation: question.translation,
        translationWordclass: question.translationWordclass
          ? `(${question.translationWordclass})`
          : ""
      }),
      [QuestionTypeEnum.MatchingPairs]: t("vocab.games.german.matchingPairsPrompt"),
      [QuestionTypeEnum.DragDrop]: t("vocab.games.german.dragDropPrompt", {
        translation: question.translation,
        translationWordclass: question.translationWordclass
          ? `(${question.translationWordclass})`
          : ""
      })
    }
  };

  return questionTypePromptMap[language][type];
};

/** Remove achievements of higher tier than the lowest in the collection */
export const filterAchievementCollection = (achievements: Achievement[]): Achievement[] => {
  const result: Achievement[] = [];

  achievements.forEach((a) => {
    // Achieved achievements don't need to be filtered
    if (a.achievedAt) {
      return result.push(a);
    }

    const existingResult = result.find((r) => r.slug === a.slug && !r.achievedAt);

    // If an ongoing achievement isn't already in the result, just add it
    if (!existingResult) {
      return result.push(a);
    }

    // This leaves us a duplicate ongoing achievement - for those, we only want to show the first ongoing tier.
    if (existingResult.index > a.index) {
      const index = result.indexOf(existingResult);
      result[index] = a;
    }
  });

  return result;
};
